import { Observable, OperatorFunction, map } from "rxjs";
import { cloneDeep, groupBy } from "lodash-es";
import { Sample } from "../samples/sample";
import type { SamplesProgress } from "./data.service";
import { SAMPLES } from "./samples.data";
import { console } from "#/log";

/**
 * Encapsulate logic for grouping and sorting samples according to
 * their completed progress and prerequisite locking.
 */
export class SampleGrouperSorter {
  /**
   * rxjs Operator for grouping and sorting samples based on Observable<SamplesProgress>.
   *
   * To also get the progress update along with the grouped samples, use {@link groupSortWithProgress}.
   */
  public static groupAndSortSamples(
    samples: Sample[] = SAMPLES,
  ): OperatorFunction<SamplesProgress, GroupedSamples> {
    return (progress: Observable<SamplesProgress>) => {
      return progress.pipe(
        map((progress) => {
          return new this(progress).groupByCategory(samples);
        }),
      );
    };
  }

  /**
   * rxjs Operator for grouping and sorting samples based on Observable<SamplesProgress>;
   * passes through the updated samples as well.
   *
   * If you only want the grouped samples (without the progress), see {@link groupAndSortSamples}.
   */
  public static groupSortWithProgress(
    samples: Sample[] = SAMPLES,
  ): OperatorFunction<SamplesProgress, [SamplesProgress, GroupedSamples]> {
    return (progress: Observable<SamplesProgress>) => {
      return progress.pipe(
        map((progress) => {
          const grouped = new this(progress).groupByCategory(samples);
          return [progress, grouped];
        }),
      );
    };
  }

  /**
   * Non-public constructor; instances are used internally from static entry points.
   */
  protected constructor(protected progress: SamplesProgress) {}

  protected getSampleProgress(sampleId: string) {
    if (this.progress && sampleId in this.progress) {
      return this.progress[sampleId].progress || 0;
    }
    return 0;
  }

  /**
   * Groups the provided sample items by the category attribute,
   * then sorts them within each category by current progress.
   * Does not mutate the provided array.
   *
   * @returns
   */
  protected groupByCategory(samples: Sample[]): GroupedSamples {
    const grouped = groupBy(cloneDeep(samples), "category");
    const sorted = this.sortGroups(grouped);
    console.debug("grouper: group made", sorted);

    return sorted;
  }

  protected sortGroups(groups: GroupedSamples): GroupedSamples {
    for (let key in groups) {
      groups[key] = this.sortByLockStateAndName(groups[key]);
    }
    return groups;
  }

  /**
   * Checks to see if sample should be locked
   * @param sample - the sample to check
   */
  protected isSampleLocked(sample: Sample): boolean {
    if (sample["availableAfter"] && sample["availableAfter"].length > 0) {
      for (let i = 0; i < sample.availableAfter.length; i++) {
        let sampleProgress = this.getSampleProgress(sample.availableAfter[i]);
        if (sampleProgress < 100 || !sampleProgress) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Sorts a list of samples by their lock state, and within their lock state by
   * their name.
   * If your linter complains about this function: It is used in the template.
   */
  protected sortByLockStateAndName(items: Array<Sample>): Array<any> {
    return items.sort((a, b) => {
      const aIsLocked = this.isSampleLocked(a);
      const bIsLocked = this.isSampleLocked(b);

      if (!aIsLocked && bIsLocked) {
        return -1;
      }
      if (aIsLocked && !bIsLocked) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });
  }
}
export type GroupedItems<T> = Record<string, Array<T>>;
export type GroupedSamples = GroupedItems<Sample>;
